import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { IResourceDatabaseFolder } from '@/api/types';
import type { IDatabaseFolderColumnNames } from '@/components/common';
import type { ITableSortingState } from '@/components/common/table/types';

import {
  Table,
  DropdownMenu,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  ClickableResourceTableCell,
} from '@/components/common';
import { ResourceDatabase } from '@/assets/icons';

interface IProps {
  items: IResourceDatabaseFolder[];
  sortingState: ITableSortingState<IDatabaseFolderColumnNames>;
  onSort: (columnName: IDatabaseFolderColumnNames) => void;
  selectDatabaseAction: (dbId: number) => void;
}

const ResourceDatabasesTable = ({ items, sortingState, onSort, selectDatabaseAction }: IProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Table type="floating">
      <StyledResourceTableHeader className="small">
        <ResourceTableHeaderCell width="26px" minWidth="26px" />
        <ResourceTableHeaderCell
          size={3}
          sortingEnabled
          sortingState={sortingState.name}
          onClick={() => onSort('name')}
          minWidth="168px"
        >{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.status}
          onClick={() => onSort('status')}
          minWidth="118px"
        >{t`resources.foldersList.headerVisibility`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={1}
          sortingEnabled
          sortingState={sortingState.numFields}
          onClick={() => onSort('numFields')}
          minWidth="68px"
        >{t`resources.foldersList.headerFields`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={1}
          sortingEnabled
          sortingState={sortingState.numViews}
          onClick={() => onSort('numViews')}
          minWidth="68px"
        >{t`resources.foldersList.headerViews`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={1}
          sortingEnabled
          sortingState={sortingState.numRecords}
          onClick={() => onSort('numRecords')}
          minWidth="68px"
        >{t`resources.foldersList.headerItems`}</ResourceTableHeaderCell>
      </StyledResourceTableHeader>
      {items.map((item, id) => (
        <StyledResourceTableRow key={id} type="floating" firstBgColor="#f9fAfb" secondBgColor="#fff" className="small">
          <ResourceTableCell width="26px" minWidth="26px" className="m-0">
            <ResourceIcon src={ResourceDatabase} />
          </ResourceTableCell>
          <ClickableResourceTableCell size={3} onClick={() => selectDatabaseAction(item.id)} minWidth="150px">
            {item.name}
          </ClickableResourceTableCell>
          <ResourceTableCell size={2} minWidth="100px">
            {t(`resources.databases.status.${item.status}`)}
          </ResourceTableCell>
          <ResourceTableCell size={1} minWidth="50px">
            {item.numFields}
          </ResourceTableCell>
          <ResourceTableCell size={1} minWidth="50px">
            {item.numViews}
          </ResourceTableCell>
          <ResourceTableCell size={1} minWidth="50px">
            {item.numRecords}
          </ResourceTableCell>
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu
              // TODO: Add option callbacks
              options={[
                {
                  label: t`resources.databases.options.generalSettings`,
                  callback: () => '',
                },
                {
                  label: t`resources.databases.options.importData`,
                  callback: () => '',
                },
                {
                  label: t`resources.databases.options.exportData`,
                  callback: () => '',
                },
                {
                  label: t`resources.databases.options.clearAllData`,
                  callback: () => '',
                },
                {
                  label: t`resources.databases.options.deleteDatabase`,
                  callback: () => '',
                },
              ]}
              separators={[0, 2]}
            />
          </ResourceTableCell>
        </StyledResourceTableRow>
      ))}
    </Table>
  );
};

export { ResourceDatabasesTable };

const ResourceIcon = styled.img`
  width: 100%;
  max-height: 16px;
`;

const StyledResourceTableHeader = styled(ResourceTableHeader)`
  padding-right: 45px;
`;

const StyledResourceTableRow = styled(ResourceTableRow)`
  padding-right: 45px;
`;
